import { Fingerprint, Globe, Key, LogOut, Send, Settings, Shield, User } from 'lucide-react';
import { useAuth } from '@/contexts/auth';
import { useModalManager } from '@/hooks/useModalManager';
import { Button } from './ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface UserMenuProps {
	onOpenPublicAccess?: () => void;
	onOpenSubmissionTokens?: () => void;
}

/**
 * UserMenu - User avatar dropdown in top right corner
 *
 * Features:
 * - Shows user's display name or username
 * - Avatar with first letter of username
 * - Dropdown with Settings and Logout
 */
export function UserMenu({ onOpenPublicAccess, onOpenSubmissionTokens }: UserMenuProps) {
	const { user, logout } = useAuth();
	const { openModal } = useModalManager();

	if (!user) return null;

	const displayName = user.display_name || user.username;
	const initial = displayName.charAt(0).toUpperCase();

	const handleLogout = async () => {
		await logout();
		window.location.href = '/login';
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" className="gap-2 px-2 h-9">
					{/* Avatar circle */}
					<div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground font-semibold text-sm">
						{initial}
					</div>
					{/* Username (hidden on small screens) */}
					<span className="hidden sm:inline text-sm font-medium">{displayName}</span>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-56">
				<DropdownMenuLabel>
					<div className="flex flex-col space-y-1">
						<p className="text-sm font-medium leading-none">{displayName}</p>
						<p className="text-xs leading-none text-muted-foreground">@{user.username}</p>
					</div>
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<DropdownMenuItem onClick={() => openModal('profile')}>
					<User className="mr-2 h-4 w-4" />
					<span>My Profile</span>
				</DropdownMenuItem>
				<DropdownMenuItem onClick={() => openModal('passkeys')}>
					<Fingerprint className="mr-2 h-4 w-4" />
					<span>Passkeys</span>
				</DropdownMenuItem>
				<DropdownMenuItem onClick={() => openModal('security')}>
					<Shield className="mr-2 h-4 w-4" />
					<span>Security</span>
				</DropdownMenuItem>
				<DropdownMenuItem onClick={() => openModal('api-keys')}>
					<Key className="mr-2 h-4 w-4" />
					<span>API Keys</span>
				</DropdownMenuItem>
			{onOpenPublicAccess && (
				<DropdownMenuItem onClick={onOpenPublicAccess}>
					<Globe className="mr-2 h-4 w-4" />
					<span>Public Access</span>
				</DropdownMenuItem>
			)}
			{onOpenSubmissionTokens && (
				<DropdownMenuItem onClick={onOpenSubmissionTokens}>
					<Send className="mr-2 h-4 w-4" />
					<span>Submission Tokens</span>
				</DropdownMenuItem>
			)}
				<DropdownMenuSeparator />
				<DropdownMenuItem onClick={() => openModal('settings')}>
					<Settings className="mr-2 h-4 w-4" />
					<span>Settings</span>
				</DropdownMenuItem>
				<DropdownMenuSeparator />
				<DropdownMenuItem
					onClick={handleLogout}
					className="text-destructive focus:text-destructive"
				>
					<LogOut className="mr-2 h-4 w-4" />
					<span>Logout</span>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
